﻿import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type { AccessToken } from '../@types/AccessToken';
import type { BaseAction } from '../@types/actions';
import type { RootState } from '../reducers';

import type { ProfileFull } from '../@types/Profile';
import * as http from '../Http';
import {
  buildAuthorizedGetRequest,
  buildAuthorizedPutRequest,
  buildGetRequest,
} from '../Http/requestTemplates';
import requestCache from '../utils/request-cache';

const REQ_ACCESS_TKN = 'REQ/ACCESS_TKN';
export const RCV_ACCESS_TKN = 'RCV/ACCESS_TKN';
type REQ_ACCESS_TKN_TYPE = BaseAction<typeof REQ_ACCESS_TKN>;
type RCV_ACCESS_TKN_TYPE = BaseAction<typeof RCV_ACCESS_TKN> & {
  response: AccessToken;
};
export const REQ_PROFILE = 'REQ/PROFILE';
export const RCV_PROFILE = 'RCV/PROFILE';
type REQ_PROFILE_TYPE = BaseAction<typeof REQ_PROFILE>;
type RCV_PROFILE_TYPE = BaseAction<typeof RCV_PROFILE> & {
  response: ProfileFull;
};
export const REQ_UPDATE_PROFILE = 'REQ/UPDATE_PROFILE';
export const RCV_UPDATE_PROFILE = 'RCV/UPDATE_PROFILE';
type REQ_UPDATE_PROFILE_TYPE = BaseAction<typeof REQ_UPDATE_PROFILE>;
type RCV_UPDATE_PROFILE_TYPE = BaseAction<typeof RCV_UPDATE_PROFILE> & {
  response: ProfileFull;
};

export const REQUEST_FAILED = 'PROFILE/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action: typeof REQ_ACCESS_TKN | typeof REQ_PROFILE | typeof REQ_UPDATE_PROFILE;
};

export type AUTH_ACTION =
  | REQ_ACCESS_TKN_TYPE
  | RCV_ACCESS_TKN_TYPE
  | REQ_PROFILE_TYPE
  | RCV_PROFILE_TYPE
  | REQ_UPDATE_PROFILE_TYPE
  | RCV_UPDATE_PROFILE_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<AUTH_ACTION>;

const createRefreshFrame = (callback: () => void) => {
  if (process.env.SSR) return null;

  const body = document.body;
  const frame = document.createElement('iframe');

  frame.src = '/auth/refresh';
  frame.setAttribute('width', '1');
  frame.setAttribute('height', '1');
  frame.setAttribute('frameborder', '0');
  frame.onload = () => {
    callback();
    body.removeChild(frame);
  };
  body.appendChild(frame);
};

export const getAccessToken = () => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_ACCESS_TKN)) {
    return;
  }
  requestCache.isPending(REQ_ACCESS_TKN, true);

  try {
    dispatch({ type: REQ_ACCESS_TKN });

    const response = await http.json<AccessToken>(
      '/api/auth/token',
      buildGetRequest({ withCookies: true }),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_ACCESS_TKN,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_ACCESS_TKN });
  }
  requestCache.isPending(REQ_ACCESS_TKN, false);
};

export const refreshAccessToken = () => (dispatch: Dispatch) =>
  createRefreshFrame(() => getAccessToken()(dispatch));

export const get = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();
  if (!auth.token || requestCache.isPending(REQ_PROFILE)) {
    return;
  }
  requestCache.isPending(REQ_PROFILE, true);

  try {
    dispatch({ type: REQ_PROFILE });

    const response = await http.json<ProfileFull>(
      '/api/profile',
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_PROFILE,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_PROFILE });
  }
  requestCache.isPending(REQ_PROFILE, false);
};

export const update =
  (profile: ProfileFull) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_UPDATE_PROFILE)) {
      return;
    }
    requestCache.isPending(REQ_UPDATE_PROFILE, true);

    try {
      dispatch({ type: REQ_UPDATE_PROFILE });

      const response = await http.json<ProfileFull>(
        '/api/profile',
        buildAuthorizedPutRequest(auth.token, { content: profile }),
      );
      http.validateResponse(response);
      dispatch({
        type: RCV_UPDATE_PROFILE,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_UPDATE_PROFILE });
    }
    requestCache.isPending(REQ_UPDATE_PROFILE, false);
  };
